import { idCodec } from "../id/brand.js";
import { REVIEWED_DESTINATION_PREFIX } from "../telephony/sip-trunk/destination-prefix.js";
import { destinationVerdict } from "../telephony/sip-trunk/destination.js";
import { isE164 } from "../telephony/sip-trunk/wire-number.js";
import { forwardDestinationRefusalOf } from "./answering-answer.js";
/** The id codec of a handoff attempt (UUID v7, minted in the application). */
export const handoffIdCodec = idCodec("HandoffId");
/** Which telephony route dials the person: the customer's own SIP trunk (primary, Q8) or a WeTalk number on the carrier. */
export const HANDOFF_PATH = ["sip_trunk", "carrier"];
/** The language-pack phrase spoken to the person before the summary (the whisper's framing line; LANG adds it). */
export const HANDOFF_FRAMING_PHRASE_KEY = "handoff.framing";
/**
 * `detail.code` values of the `transfer` conversation event a handoff writes. `handoff_priority_message` is a web chat
 * or browser voice conversation whose caller asked for a person: no path is dialled, a message follows (design
 * §30.6.23; plan-018 HO-CORE).
 */
export const HANDOFF_TRANSFER_DETAIL_CODE = [
    "handoff_started",
    "handoff_answered",
    "handoff_joined",
    "handoff_ended",
    "handoff_not_joined",
    "handoff_priority_message",
];
/** Why a setting's shape is refused (`400 validation_failed` with the field). */
export const HANDOFF_SETTING_REFUSAL = ["destination_missing", "language_missing", "destination_not_e164"];
/**
 * The shape refusal of a setting, or `null`. Switching off is always a valid shape: a stored destination and language
 * may stay, so switching back on needs no retyping.
 */
export function handoffSettingRefusalOf(setting) {
    if (setting.destinationE164 !== null && !isE164(setting.destinationE164))
        return "destination_not_e164";
    if (!setting.enabled)
        return null;
    if (setting.destinationE164 === null)
        return "destination_missing";
    if (setting.languageCode === null)
        return "language_missing";
    return null;
}
/** The refusal of `e164` as a handoff destination on `rule`'s path, or `null` when the path may dial it. */
export function handoffDestinationRefusalOf(e164, rule) {
    if (rule.kind === "carrier")
        return forwardDestinationRefusalOf(e164);
    const verdict = destinationVerdict(e164, rule.allowedCountry, REVIEWED_DESTINATION_PREFIX);
    if (!verdict.allowed)
        return verdict.rule;
    if (rule.trunkNumber.includes(e164))
        return "trunk_number";
    return null;
}
/** Why a handoff language cannot voice the whisper (`422 handoff_language_unavailable`). */
export const HANDOFF_LANGUAGE_REFUSAL = ["not_configured", "framing_missing"];
/**
 * The refusal of `language` as the person's language, or `null`: it must be one of the version's languages (the
 * whisper is voiced with `voiceOf(config, language)`) and its pack must carry the framing line. There is no fallback
 * language (CLAUDE.md §7).
 */
export function handoffLanguageRefusalOf(language, configured, packPhrase) {
    if (!configured.includes(language))
        return "not_configured";
    const framing = packPhrase[HANDOFF_FRAMING_PHRASE_KEY];
    if (framing === undefined || framing.trim() === "")
        return "framing_missing";
    return null;
}
//# sourceMappingURL=handoff.js.map